import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ChefHat, ShoppingCart, Calendar, TrendingDown, Plus } from 'lucide-react';
import { toast } from 'sonner';
import useStore from '../store';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, setHousehold } = useStore();
  const [loading, setLoading] = useState(true);
  const [household, setLocalHousehold] = useState(null);
  const [plans, setPlans] = useState([]);
  const [latestPlan, setLatestPlan] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let userData = location.state?.user;
        if (!userData) {
          const userResponse = await axios.get(`${API}/auth/me`, { withCredentials: true });
          userData = userResponse.data;
          setUser(userData);
        } else {
          setUser(userData);
        }

        const householdResponse = await axios.get(`${API}/households`, { withCredentials: true });
        if (householdResponse.data.household) {
          setLocalHousehold(householdResponse.data.household);
          setHousehold(householdResponse.data.household);

          const plansResponse = await axios.get(
            `${API}/week-plans/${householdResponse.data.household.household_id}`,
            { withCredentials: true }
          );
          setPlans(plansResponse.data.plans);
          if (plansResponse.data.plans.length > 0) {
            setLatestPlan(plansResponse.data.plans[0]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        if (error.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate, location.state, setUser, setHousehold]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }
  if (!household) { navigate("/onboarding"); return null; }

  const savingsEstimate = latestPlan?.saving_estimate_vs_country_avg_eur || 0;
  const totalCost = latestPlan?.total_cost_estimate_eur || 0;

  return (
    <div className="py-4 sm:py-8">
      <div className="container mx-auto px-3 sm:px-4" data-testid="dashboard">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-[#2C3E2D] mb-4 sm:mb-8">
          {t('dashboard.welcome', { name: user?.name?.split(' ')[0] || '' })}
        </h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-8">
          <Card className="bg-gradient-to-br from-primary to-[#81B29A] text-white" data-testid="savings-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <TrendingDown className="w-5 h-5" />
                {t('dashboard.savings_meter')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl sm:text-4xl font-bold mb-2" data-testid="savings-amount">
                {savingsEstimate >= 0 ? '+' : ''}&euro;{savingsEstimate.toFixed(2)}
              </div>
              <p className="text-sm text-white/80">{t('dashboard.vs_average')}</p>
              {totalCost > 0 && (
                <p className="text-xs text-white/70 mt-2">{t('dashboard.your_week', { cost: totalCost.toFixed(2) })}</p>
              )}
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow" data-testid="todays-menu-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ChefHat className="w-5 h-5 text-primary" />
                {t('dashboard.todays_menu')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {latestPlan ? (
                <div>
                  <p className="text-lg sm:text-2xl font-semibold mb-2">{t('dashboard.day_menu')}</p>
                  <p className="text-sm text-gray-600">{t('dashboard.view_plan_details')}</p>
                </div>
              ) : (
                <div>
                  <p className="text-gray-500 mb-4">{t('dashboard.no_plan')}</p>
                  <Button 
                    size="sm" 
                    className="bg-primary hover:bg-primary-hover text-white"
                    onClick={() => navigate('/plan/create')}
                    data-testid="create-plan-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('dashboard.create_plan')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow" data-testid="quick-actions-card">
            <CardHeader>
              <CardTitle>{t('dashboard.quick_actions')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate('/plan/create')}
                data-testid="quick-create-plan"
              >
                <Calendar className="w-4 h-4 mr-2" />
                {t('dashboard.new_weekly_plan')}
              </Button>
              {latestPlan && (
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate(`/shopping/${latestPlan.plan_id}`)}
                  data-testid="quick-view-shopping"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  {t('dashboard.view_shopping')}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('dashboard.recent_plans')}</CardTitle>
            </CardHeader>
            <CardContent>
              {plans.length > 0 ? (
                <div className="space-y-3">
                  {plans.slice(0, 3).map((plan) => (
                    <div 
                      key={plan.plan_id} 
                      className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                      onClick={() => navigate(`/plan/${plan.plan_id}`)}
                      data-testid={`plan-item-${plan.plan_id}`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-semibold">{t('dashboard.week_of', { date: plan.start_date })}</p>
                          <p className="text-sm text-gray-600">
                            {plan.dishes_per_day?.length || 0} {t('dashboard.dishes_cost', { count: plan.dishes_per_day?.length || 0 }).split(' ').slice(1).join(' ')} &bull; &euro;{plan.total_cost_estimate_eur?.toFixed(2)}
                          </p>
                        </div>
                        <Calendar className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">{t('dashboard.no_plans_yet')}</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('dashboard.your_subscription')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-4">
                <div className="inline-block bg-primary/10 px-6 py-2 rounded-full mb-4">
                  <span className="text-xl font-bold text-primary capitalize" data-testid="user-tier-display">{user?.tier || 'free'}</span>
                </div>
                <p className="text-gray-600 mb-4">{t('dashboard.plan_label')}</p>
                {(user?.tier === 'free' || !user?.tier) && (
                  <Button 
                    className="bg-secondary hover:bg-secondary-hover text-white"
                    onClick={() => navigate('/pricing')}
                    data-testid="upgrade-btn"
                  >
                    {t('dashboard.upgrade_premium')}
                  </Button>
                )}
                {user?.tier && user.tier !== 'free' && (
                  <p className="text-sm text-gray-600">{t('dashboard.thank_member', { tier: user.tier })}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
